import { getPublicApiBaseCandidates } from "../utils/publicApi";

// REACT_APP_API_URL full base (including /api); e.g. https://ctfglobal.in/api/backend/public/api
const API_BASE =
  getPublicApiBaseCandidates()[0] || "https://ctfglobal.in/api/backend/public/api";

function getToken() {
  return localStorage.getItem("ctf_token");
}

function getRole() {
  return localStorage.getItem("ctf_role");
}

export async function api(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    Accept: "application/json",
    ...options.headers,
  };
  // When body is FormData, do not set Content-Type so browser sets multipart/form-data with boundary
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn(`No token found for request: ${endpoint}`);
  }
  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (e) {
    throw new Error("Backend not reachable. Is the server running? (php artisan serve)");
  }
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      res.ok
        ? "Invalid JSON from server"
        : `Server error (${res.status}). Run: php artisan migrate. Check: backend/storage/logs/laravel.log`
    );
  }
  if (!res.ok) {
    // On 401/403 for protected routes, clear auth and redirect to login (same for all roles)
    const isAuthError = res.status === 401 || res.status === 403;
    const isProtectedRoute = endpoint.startsWith("/admin") || endpoint.startsWith("/member") || endpoint.startsWith("/student");
    const isNotLogin = !endpoint.includes("/login");

    if (isAuthError && isProtectedRoute && isNotLogin) {
      const msg = String(data?.message || data?.error || "").toLowerCase();
      const isDeactivated = msg.includes("account is deactivated") || msg.includes("read-only") || msg.includes("deactivated");
      localStorage.removeItem("ctf_token");
      localStorage.removeItem("ctf_role");
      localStorage.removeItem("ctf_user");

      // For deactivated accounts, keep the user on their page (read-only),
      // so they can still view history.
      if (!isDeactivated) {
        const path = window.location.pathname;
        if (path.startsWith("/admin")) {
          window.location.href = "/admin/login";
          return;
        }
        if (path.startsWith("/member")) {
          window.location.href = "/member/login";
          return;
        }
        if (path.startsWith("/student")) {
          window.location.href = "/student/login";
          return;
        }
      }
    }

    const msg = data.message || data.error;
    const errs = data.errors;
    const detail =
      errs && typeof errs === "object" ? Object.values(errs).flat().join(" ") : null;
    const err = new Error(detail || msg || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const authApi = {
  adminLogin: (email, password) =>
    api("/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  memberLogin: (email, password) =>
    api("/member/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  studentLogin: (email, password) =>
    api("/student/login", {
      method: "POST",
      // Backend expects `student_id` key; we pass email here so login is via email
      body: JSON.stringify({ student_id: email, password }),
    }),
  adminLogout: () => api("/admin/logout", { method: "POST" }),
  memberLogout: () => api("/member/logout", { method: "POST" }),
  studentLogout: () => api("/student/logout", { method: "POST" }),
};

/** Base API URL used for media + JSON endpoints. */
export function getApiBaseUrl() {
  return API_BASE;
}

/** Storage file URL - served through the backend media endpoint. */
function getPreferredMediaBaseUrl() {
  const candidates = getPublicApiBaseCandidates();

  if (typeof window === "undefined") {
    return candidates[0] || API_BASE;
  }

  const currentOrigin = window.location.origin;
  const currentHostname = window.location.hostname;

  for (const base of candidates) {
    if (!base) continue;

    try {
      const parsed = new URL(base);
      if (parsed.origin === currentOrigin || parsed.hostname === currentHostname) {
        return base;
      }
    } catch {
      // Ignore invalid candidate URLs and keep searching.
    }
  }

  return candidates[0] || API_BASE;
}

export function getStorageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const base = getPreferredMediaBaseUrl().replace(/\/$/, "");
  const clean = path.replace(/^\/+/, "");
  const normalized = clean.startsWith("storage/") ? clean : `storage/${clean}`;
  return `${base}/media/${normalized}`;
}

/** Course image URL - use for dashboard, course details, AddCourse */
export function getCourseImageUrl(img) {
  if (!img) return null;
  if (img.startsWith("http")) {
    try {
      const parsed = new URL(img);
      if (
        parsed.pathname.includes("/storage/") ||
        parsed.pathname.includes("/media/") ||
        parsed.hostname === "localhost" ||
        parsed.hostname === "127.0.0.1"
      ) {
        return getStorageUrl(parsed.pathname);
      }
    } catch {
      // Fall through and use the original URL.
    }
    return img;
  }
  return getStorageUrl(img.startsWith("/") ? img.slice(1) : img);
}

export { getToken, getRole };
export { API_BASE };
